<template>
  <div class="sf-layer-border sf-layer-rounded sf-below-2 sf-hover">
    <div class="sf-level sf-level-between sf-pad-3 sf-layer-border-bottom">
      <div class="sf-level">
        <div class="sf-font-m sf-fwdb sf-c-text-dark">
          <i
            :position="value.position"
            class="sf-grab sf-c-text-light icon i-hamburger drag-handle"
          />
          <span class="sf-pad-l-1">{{ value.name[currentLocale] }}</span>
        </div>
        <GraphValueDateLabel :value="value" />
      </div>

      <div class="sf-level">
        <SfButtonDs
          variant="danger"
          size="sm"
          class="sf-hover-item"
          :class="{ 'sf-hover-item-hide': readOnly }"
          @click="deleteGraphValue(value.key)"
        >
          {{ I18n.t('js.report_graph.edit.destroy') }}
        </SfButtonDs>
        <i
          class="sf-c-text-light sf-pointer fa"
          :class="value.collapsed ? 'fa-chevron-up' : 'fa-chevron-down'"
          @click="setValueAttributes({ collapsed: !value.collapsed })"
        />
      </div>
    </div>

    <div v-show="!value.collapsed" class="sf-pad-3">
      <div class="sf-below-2">
        <label for="name" class="sf-form-label">{{
          I18n.t('shared.name')
        }}</label>
        <input
          v-model="valueName"
          name="name"
          class="sf-form-input sf-full sf-below-1"
          :class="{ 'sf-disabled': readOnly }"
          type="text"
          :placeholder="I18n.t('shared.name')"
        />
        <div
          v-show="
            attributeErrors({ position: value.position, attribute: 'name' })
          "
          class="sf-alert sf-alert-danger sf-pad-1 sf-level-vertical sf-below-2"
        >
          <div
            v-for="(errorMessage, index) in attributeErrors({
              position: value.position,
              attribute: 'name',
            })"
            :key="index"
            class="sf-level-item"
          >
            {{ errorMessage }}
          </div>
        </div>

        <div
          v-show="isGraphTypeLinechart"
          class="sf-grid sf-grid-fluid sf-grid-gutter-small sf-below-1"
        >
          <div class="sf-row">
            <div
              v-for="(option, index) in seriesTypeOptions"
              :key="index"
              class="sf-col sf-xs-3 sf-pointer"
            >
              <div
                :id="option.name"
                class="sf-pad-2 sf-layer-rounded sf-align-c"
                :class="classFor(option.name)"
                @click="setValueAttributes({ seriesType: option.name })"
              >
                <i :class="option.iconClass" />
              </div>
            </div>
          </div>
        </div>
        <div
          v-show="
            attributeErrors({
              position: value.position,
              attribute: 'seriesType',
            })
          "
          class="sf-alert sf-alert-danger sf-pad-1 sf-level-vertical sf-below-2"
        >
          <div
            v-for="(errorMessage, index) in attributeErrors({
              position: value.position,
              attribute: 'seriesType',
            })"
            :key="index"
            class="sf-level-item"
          >
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="sf-below-2">
        <div class="sf-form-label">
          {{ I18n.t('js.report_graph.edit.graph_values.value_label') }}
        </div>

        <div
          :for="value.key + '-reportTag'"
          class="sf-level sf-level-between sf-below-1"
        >
          <label
            :for="value.key + '-reportTag'"
            class="sf-form-check sf-form-check-rounded sf-inline-block"
          >
            <input
              :id="value.key + '-reportTag'"
              v-model="formulaType"
              type="radio"
              value="reportTag"
              :disabled="readOnly"
            />
            <span class="sf-form-check-content sf-level">
              <span class="sf-form-check-indicator" />
              <span>{{
                I18n.t('js.report_graph.edit.graph_values.report_tag_label')
              }}</span>
            </span>
          </label>

          <select
            v-model="reportTag"
            class="sf-form-select sf-form-input sf-level-item-full"
            :class="{ 'sf-disabled': readOnly }"
          >
            <option
              v-for="(tag, index) in reportTemplate.reportTags"
              :key="index"
              :value="tag"
            >
              {{ tag }}
            </option>
          </select>
        </div>

        <div
          :for="value.key + '-formula'"
          class="sf-level sf-level-between sf-below-1"
        >
          <label
            :for="value.key + '-formula'"
            class="sf-form-check sf-form-check-rounded sf-inline-block"
          >
            <input
              :id="value.key + '-formula'"
              v-model="formulaType"
              type="radio"
              value="formula"
              :disabled="readOnly"
            />
            <span class="sf-form-check-content sf-level">
              <span class="sf-form-check-indicator" />
              <span>{{
                I18n.t('js.report_graph.edit.graph_values.formula_label') +
                '&nbsp;'.repeat(5)
              }}</span>
            </span>
          </label>

          <input
            v-model="valueFormula"
            name="formulaInput"
            type="text"
            class="sf-form-input sf-level-item-full"
            :class="{ 'sf-disabled': readOnly }"
          />
        </div>

        <div
          v-show="
            attributeErrors({ position: value.position, attribute: 'formula' })
          "
          class="sf-alert sf-alert-danger sf-pad-1 sf-level-vertical sf-below-2"
        >
          <div
            v-for="(errorMessage, index) in attributeErrors({
              position: value.position,
              attribute: 'formula',
            })"
            :key="index"
            class="sf-level-item"
          >
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="sf-below-2">
        <div class="sf-level">
          <div class="sf-form-select-wrapper sf-level-item-full">
            <select
              v-model="valueRelative"
              name="value_relative"
              class="sf-form-select"
              :class="{ 'sf-disabled': readOnly }"
            >
              <option
                v-for="(option, index) in relativeOptions"
                :key="index"
                :value="option.value"
                :selected="option.value === value.relative"
              >
                {{ option.name }}
              </option>
            </select>
          </div>
          <div class="sf-form-select-wrapper sf-level-item-full">
            <select
              v-model="valueBudget"
              name="value_budget"
              class="sf-form-select"
              :class="{ 'sf-disabled': readOnly }"
            >
              <option
                v-for="(option, index) in budgetOptions"
                :key="index"
                :value="option.value"
                :selected="option.value === value.budget"
              >
                {{ option.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div v-show="isGraphTypeLinechart" class="sf-below-2">
        <SfCheckbox
          v-model="valueType"
          :disabled="readOnly"
          :label="I18n.t('js.report_graph.edit.graph_values.value_type_label')"
        ></SfCheckbox>
      </div>

      <div v-show="isGraphTypeLinechart" class="sf-below-2">
        <label for="time_offset" class="sf-form-label">{{
          I18n.t('js.report_graph.edit.graph_values.time_series_offset_label')
        }}</label>
        <div
          v-tooltip="timeSeriesOffset"
          class="sf-form-select-wrapper sf-full"
        >
          <select
            v-model="valueDelta"
            name="time_offset"
            class="sf-form-select"
            :class="{
              'sf-disabled': readOnly || notEnoughValuesForTimeSeriesOffset,
            }"
          >
            <option
              v-for="(option, index) in deltaOptions"
              :key="index"
              :value="option.value"
              :selected="option.value === value.delta"
            >
              {{ option.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox'

import GraphValueDateLabel from './graph-value-date-label.vue'

const SERIES_TYPE_OPTIONS = [
  { name: 'line', iconClass: 'fa fa-line-chart' },
  { name: 'area', iconClass: 'fa fa-area-chart' },
  { name: 'bar', iconClass: 'fa fa-bar-chart' },
  { name: 'stacking', iconClass: 'i-graph-stacking' },
]
const PERCENTAGE_VALUE = 'percentage'
const MAX_DELTA_DEVIATION = 5

export default {
  components: {
    GraphValueDateLabel,
    SfButtonDs,
    SfCheckbox,
  },
  props: ['value', 'deleteGraphValue'],
  data() {
    return {
      seriesTypeOptions: SERIES_TYPE_OPTIONS,
    }
  },
  computed: {
    ...mapState([
      'currentLocale',
      'readOnly',
      'reportTemplate',
      'canSelectBudget',
    ]),
    ...mapGetters([
      'graphValuesErrors',
      'isGraphTypeLinechart',
      'notEnoughValuesForTimeSeriesOffset',
      'budgetOptions',
    ]),
    relativeOptions() {
      return [
        {
          value: 'PTD',
          name: I18n.t('js.report_graph.edit.relative_option_labels.PTD'),
        },
        {
          value: 'YTD',
          name: I18n.t('js.report_graph.edit.relative_option_labels.YTD'),
        },
        {
          value: 'RYTD',
          name: I18n.t('js.report_graph.edit.relative_option_labels.RYTD'),
        },
      ]
    },
    deltaOptions() {
      return Array.from({ length: 2 * MAX_DELTA_DEVIATION + 1 }, (_, i) => {
        const count = i - MAX_DELTA_DEVIATION
        let i18nKey = 'no_delta'
        if (count > 0) {
          i18nKey = 'delta_positive_value'
        } else if (count < 0) {
          i18nKey = 'delta_negative_value'
        }
        return {
          name: I18n.t(`js.report_graph.edit.graph_values.${i18nKey}`, {
            count: Math.abs(count),
          }),
          value: count,
        }
      })
    },
    timeSeriesOffset() {
      if (this.notEnoughValuesForTimeSeriesOffset && !this.readOnly) {
        return I18n.t(
          'js.report_graph.edit.graph_values.time_series_offset_tooltip'
        )
      }

      return ''
    },
    valueName: {
      get() {
        return this.value.name[this.currentLocale]
      },
      set(newNameValue) {
        this.setGraphValueName({
          name: newNameValue,
          position: this.value.position,
        })
      },
    },
    formulaType: {
      get() {
        return this.value.formulaType
      },
      set(newFormulaType) {
        this.setValueAttributes({ formulaType: newFormulaType })
      },
    },
    reportTag: {
      get() {
        return this.value.reportTag
      },
      set(newReportTag) {
        this.setValueAttributes({
          reportTag: newReportTag,
          formulaType: 'reportTag',
        })
      },
    },
    valueFormula: {
      get() {
        return this.value.formula
      },
      set(newFormulaValue) {
        this.setValueAttributes({
          formula: newFormulaValue,
          formulaType: 'formula',
        })
      },
    },
    valueRelative: {
      get() {
        return this.value.relative
      },
      set(newRelativeValue) {
        this.setValueAttributes({ relative: newRelativeValue })
      },
    },
    valueBudget: {
      get() {
        if (this.canSelectBudget) {
          return this.value.budget && this.value.budgetSetId
        } else {
          return this.value.budget
        }
      },
      set(newBudgetValue) {
        this.setValueAttributes({ budget: !!newBudgetValue })
        if (this.canSelectBudget) {
          if (newBudgetValue === false) {
            newBudgetValue = null
          } // nullify budgetSetId for actuals
          this.setValueAttributes({ budgetSetId: newBudgetValue })
        }
      },
    },
    valueType: {
      get() {
        return this.value.valueType === PERCENTAGE_VALUE
      },
      set(newValueType) {
        this.setValueAttributes({
          valueType: newValueType ? PERCENTAGE_VALUE : '',
        })
      },
    },
    valueDelta: {
      get() {
        return this.value.delta
      },
      set(newValueDelta) {
        this.setValueAttributes({ delta: newValueDelta })
      },
    },
  },
  methods: {
    ...mapActions(['setGraphValueName', 'setGraphValueAttributes']),
    attributeErrors({ position, attribute }) {
      const valueErrors = this.graphValuesErrors.find((error) => {
        return error.position === position
      })

      if (valueErrors && valueErrors[attribute]) {
        if (this.value.collapsed) {
          this.setValueAttributes({ collapsed: false })
        }
        return valueErrors[attribute]
      }
    },
    classFor(seriesType) {
      return {
        'sf-bg-primary-subtle sf-c-primary':
          this.value.seriesType === seriesType,
        'sf-bg-snow sf-c-text': this.value.seriesType !== seriesType,
      }
    },
    setValueAttributes(attributes) {
      if (this.readOnly) {
        return
      }
      this.setGraphValueAttributes({
        position: this.value.position,
        attributes,
      })
    },
  },
}
</script>
